import React, { useRef, useState } from 'react';

import { Box, Button, Checkbox, Flex, Paper, Space, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { TimedProgress } from 'components/TimedProgressBar';
import { VirtualListScrollArea } from 'components/VirtualListScrollArea';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { WebsiteService } from 'services/website-service';
import { MAX_WEBSITE_SUB_LINKS } from 'shared/constants/general-constants';
import { Website } from 'shared/interfaces/Website';
import { requiredInputValidator, validateUrl } from 'shared/utils/form-validators';

interface WebsiteSelectorProps {
  onWebsiteAdd: (website: Website) => void;
}

export function WebsiteSelector({ onWebsiteAdd }: WebsiteSelectorProps) {
  const { t } = useTranslation();
  const [subLinks, setSubLinks] = React.useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedSubLinks, setSelectedSubLinks] = React.useState<Set<string>>(new Set());
  const subLinksScrollArea = useRef<HTMLDivElement>(null);
  const [isSelectingSubLinks, setIsSelectingSubLinks] = React.useState(false);
  const [hasTooManySublinksSelected, setHasTooManySublinksSelected] = React.useState(false);

  const getSubLinksMutation = useMutation({
    mutationFn: WebsiteService.getSubLinks,
    onSuccess: (subLinks: string[]) => {
      setSubLinks([...subLinks]);
      if (subLinks.length == 0) {
        NotificationService.showResult(false, 'FEED_BACK.WEBSITE.SUB_LINKS.NO_SUB_LINKS');
        setIsSelectingSubLinks(false);
      }
    },
    onError: () => {
      setIsSelectingSubLinks(false);
      NotificationService.showResult(false, 'FEED_BACK.WEBSITE.SUB_LINKS.FAILURE');
    }
  });

  const baseUrlForm = useForm({
    initialValues: { url: '' },
    validate: {
      url: (value) => requiredInputValidator(value) || validateUrl(value)
    }
  });

  const onSubLinkClick = (subLink: string) => {
    if (selectedSubLinks.has(subLink)) {
      selectedSubLinks.delete(subLink);
      if (selectedSubLinks.size <= MAX_WEBSITE_SUB_LINKS) {
        setHasTooManySublinksSelected(false);
      }
    } else {
      selectedSubLinks.add(subLink);
      if (selectedSubLinks.size > MAX_WEBSITE_SUB_LINKS) {
        setHasTooManySublinksSelected(true);
      }
    }
    setSelectedSubLinks(new Set(selectedSubLinks));
  };

  const addWebsite = () => {
    if (selectedSubLinks.size <= 0 || hasTooManySublinksSelected) return;
    if (getSubLinksMutation.isLoading) return;

    const validation = baseUrlForm.validate();
    if (validation.hasErrors) return;

    const website = { url: baseUrlForm.values.url, subLinks: Array.from(selectedSubLinks.values()) };
    onWebsiteAdd(website);

    baseUrlForm.reset();
    setSubLinks([]);
    setSelectedSubLinks(new Set());
    setIsSelectingSubLinks(false);
  };

  const addSingleUrl = () => {
    const validation = baseUrlForm.validate();
    if (validation.hasErrors) return;

    const website = { url: baseUrlForm.values.url, subLinks: [] };
    onWebsiteAdd(website);

    baseUrlForm.reset();
  };

  const getSublinks = () => {
    const validation = baseUrlForm.validate();
    if (validation.hasErrors) return;
    getSubLinksMutation.mutate(baseUrlForm.values.url);
    setIsSelectingSubLinks(true);
  };

  const debouncedSearch = debounce((value: string) => setSearchFilter(value), 100);

  return (
    <Flex w="100%" h="100%" px={20} pt={50} pb={20} direction="column" align="center" justify="center">
      {isSelectingSubLinks ? (
        <>
          {getSubLinksMutation.isLoading ? (
            <>
              <Space h={20} />
              <Text size={16}>{t('BATCH_RESOURCE_SELECTOR.WEBSITE.FETCHING_LINKS_INFO')}</Text>
              <Space h={20} />
              <TimedProgress />
            </>
          ) : (
            <>
              <Title order={3}>{t('BATCH_RESOURCE_SELECTOR.WEBSITE.SELECT_SUBLINKS')}</Title>
              <Space h={20} />
              <Flex
                w="100%"
                direction="column"
                align="center"
                h={subLinks.length > 0 && !getSubLinksMutation.isLoading ? 'calc(100% - 130px)' : 0}
                style={{
                  transition: 'height 0.5s ease'
                }}>
                <Flex direction="row" w="95%" justify="space-between" align="center">
                  {subLinks.length > 0 && !getSubLinksMutation.isLoading && (
                    <TextInput
                      style={{
                        flexGrow: 1
                      }}
                      mr={10}
                      w="100%"
                      placeholder={t('BATCH_RESOURCE_SELECTOR.WEBSITE.FILTER_SUB_LINKS_FOUND')}
                      onChange={(event) => debouncedSearch(event.target.value)}
                    />
                  )}
                  <Checkbox
                    checked={selectedSubLinks.size === subLinks.length}
                    onChange={() => {
                      if (selectedSubLinks.size === subLinks.length) {
                        setSelectedSubLinks(new Set());
                        setHasTooManySublinksSelected(false);
                      } else {
                        if (subLinks.length > MAX_WEBSITE_SUB_LINKS) {
                          setHasTooManySublinksSelected(true);
                        }
                        setSelectedSubLinks(new Set(subLinks));
                      }
                    }}
                    styles={{
                      label: {
                        paddingLeft: 10
                      }
                    }}
                    mr={5}
                    miw="fit-content"
                    label={t('APP.ACTIONS.SELECT_ALL')}
                  />
                </Flex>

                <Space h={15} />

                <VirtualListScrollArea
                  w="95%"
                  offsetScrollbars
                  pr={5}
                  viewportRef={subLinksScrollArea}
                  h={subLinks.length > 0 && !getSubLinksMutation.isLoading ? 'calc(100% - 50px)' : 0}
                  sx={{
                    transition: 'height 0.5s ease'
                  }}
                  items={subLinks
                    .filter((subLink) => subLink.includes(searchFilter))
                    .map((subLink, key: number) => {
                      return (
                        <Paper
                          withBorder
                          className="g-hover-style"
                          onClick={() => onSubLinkClick(subLink)}
                          p={10}
                          mb={10}
                          key={key}>
                          <Flex direction="row" align="center">
                            <Checkbox
                              onClick={() => onSubLinkClick(subLink)}
                              onChange={() => onSubLinkClick(subLink)}
                              size="xs"
                              checked={selectedSubLinks.has(subLink)}
                            />
                            <Space w={10} />
                            <Text>{subLink}</Text>
                          </Flex>
                        </Paper>
                      );
                    })}>
                  {}
                </VirtualListScrollArea>
                {hasTooManySublinksSelected && (
                  <Box mt={20} mb={20}>
                    <Text mt={5} color="red" align="center">
                      {t('BATCH_RESOURCE_SELECTOR.WEBSITE.MAX_SUB_LINKS', {
                        maxSubLinks: MAX_WEBSITE_SUB_LINKS,
                        sublinksCount: selectedSubLinks.size
                      })}
                    </Text>
                  </Box>
                )}
              </Flex>

              <Space h={20} />

              <Flex w="95%" direction="row" justify="right">
                <Button onClick={addWebsite}>{t('BATCH_RESOURCE_SELECTOR.WEBSITE.ADD_WEBSITE')}</Button>
              </Flex>
            </>
          )}
        </>
      ) : (
        <>
          <Title order={3}>{t('BATCH_RESOURCE_SELECTOR.WEBSITE.ENTER_WEBSITE_URL')}</Title>
          <Space h={20} />
          <Flex direction="column" gap="xl" w="50%" h="100">
            <TextInput
              onKeyDown={(event) => {
                if (event.key === 'Enter') getSublinks();
              }}
              placeholder={t('BATCH_RESOURCE_SELECTOR.WEBSITE.PLACEHOLDER')}
              {...baseUrlForm.getInputProps('url')}></TextInput>
            <Flex direction="row" gap="lg" w="100%" justify="space-between">
              <Button w="100%" onClick={addSingleUrl}>
                {t('BATCH_RESOURCE_SELECTOR.WEBSITE.ADD_SINGLE_LINK')}
              </Button>
              <Button w="100%" onClick={getSublinks}>
                {t('BATCH_RESOURCE_SELECTOR.WEBSITE.FETCH_LINKS')}
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
}
